import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Drawer, Spin, message } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import DocumentViewer from '../DocumentViewer.jsx';
import CommonMailComposer, { resolveTemplateText } from './CommonMailComposer.jsx';
import { parseEmails, areValidEmails } from './mailUtils.js';
import { sendBulkMail } from '../../services/dropdownApi.js';
import { getRuntimeEmailTemplate, getRuntimeEmailTemplates } from '../../services/emailTemplateApi.js';
import { getPaymentInvoice } from '../../services/paymentInvoiceApi.js';

function firstText(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text && text !== 'undefined' && text !== 'null') return text;
  }
  return '';
}

async function urlToFile(url, fileName, contentType) {
  if (!url) return null;
  const response = await fetch(url);
  if (!response.ok) throw new Error('Unable to load invoice attachment');
  const blob = await response.blob();
  return new File([blob], fileName || 'payment-invoice.html', { type: contentType || blob.type || 'text/html' });
}

function defaultMail(invoice) {
  const courseName = firstText(invoice?.courseName, 'your course');
  const invoiceNumber = firstText(invoice?.invoiceNumber, invoice?.invoiceFileName, invoice?.transactionId);
  return {
    subject: `Payment invoice - ${courseName}`,
    contents: `<p>Hello ${firstText(invoice?.recipientName, 'Student')},</p><p>Please find attached the invoice for your successful payment${invoiceNumber ? ` (${invoiceNumber})` : ''}.</p><p>Thank you.</p>`,
  };
}

export default function PaymentInvoicePopup() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [templateId, setTemplateId] = useState('');
  const [loading, setLoading] = useState(true);
  const [mailOpen, setMailOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [mail, setMail] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    contents: '',
    attachments: [],
    showCc: false,
    showBcc: false,
  });

  const invoiceDocument = useMemo(() => {
    if (!invoice?.invoiceUrl) return [];
    return [{
      id: invoice.invoiceS3Key || invoice.invoiceUrl,
      name: invoice.invoiceFileName || 'payment-invoice.html',
      url: invoice.invoiceUrl,
      type: invoice.invoiceContentType || 'html',
    }];
  }, [invoice]);

  useEffect(() => {
    let cancelled = false;

    async function loadInvoice() {
      setLoading(true);
      try {
        const data = await getPaymentInvoice(id);
        if (cancelled) return;
        setInvoice(data);

        const fallback = defaultMail(data);
        const attachment = await urlToFile(data?.invoiceUrl, data?.invoiceFileName, data?.invoiceContentType).catch(() => null);
        const baseMail = {
          to: firstText(data?.recipientEmail),
          cc: '',
          bcc: '',
          subject: fallback.subject,
          contents: fallback.contents,
          attachments: attachment ? [attachment] : [],
          showCc: false,
          showBcc: false,
        };

        const templates = await getRuntimeEmailTemplates({ module: 'payments', action: 'invoice' }).catch(() => []);
        const selectedTemplate = templates[0];
        if (!selectedTemplate?._id) {
          setMail(baseMail);
          return;
        }

        setTemplateId(String(selectedTemplate._id));
        const template = await getRuntimeEmailTemplate(selectedTemplate._id).catch(() => null);
        if (!template) {
          setMail(baseMail);
          return;
        }
        setMail({
          ...baseMail,
          to: firstText(baseMail.to, ...(template.toMail ?? []).map((email) => resolveTemplateText(email, template.placeholders, data))),
          cc: (template.ccMail ?? []).map((email) => resolveTemplateText(email, template.placeholders, data)).filter(Boolean).join(', '),
          bcc: (template.bccMail ?? []).map((email) => resolveTemplateText(email, template.placeholders, data)).filter(Boolean).join(', '),
          subject: firstText(resolveTemplateText(template.subject, template.placeholders, data), baseMail.subject),
          contents: firstText(resolveTemplateText(template.body, template.placeholders, data), baseMail.contents),
          showCc: !!template.ccMail?.length,
          showBcc: !!template.bccMail?.length,
        });
      } catch (error) {
        if (!cancelled) message.error(error?.message || 'Unable to load invoice');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadInvoice();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const canSend = parseEmails(mail.to).length > 0
    && areValidEmails(mail.to)
    && areValidEmails(mail.cc)
    && areValidEmails(mail.bcc)
    && mail.subject.trim()
    && mail.contents.trim();

  async function handleSend() {
    if (!canSend || sending) return;
    setSending(true);
    try {
      await sendBulkMail({
        jobId: '',
        toMail: parseEmails(mail.to),
        ccMail: parseEmails(mail.cc),
        bccMail: parseEmails(mail.bcc),
        subject: mail.subject,
        contents: mail.contents,
        attachments: mail.attachments,
      });
      message.success('Invoice mail sent successfully');
      setMailOpen(false);
    } catch (error) {
      message.error(error?.message || 'Failed to send invoice mail');
    } finally {
      setSending(false);
    }
  }

  return (
    <Drawer
      open
      placement="right"
      onClose={() => navigate(-1)}
      width="min(1040px, 96vw)"
      title="Payment Invoice"
      extra={<Button type="primary" icon={<MailOutlined />} onClick={() => setMailOpen(true)} disabled={!invoice}>Send Mail</Button>}
      destroyOnClose
    >
      <Spin spinning={loading} tip="Loading invoice...">
        {!loading && !invoiceDocument.length ? (
          <Alert type="warning" message="Invoice document is not available for this payment." showIcon />
        ) : (
          <div style={{ height: 'calc(100vh - 140px)' }}>
            <DocumentViewer documents={invoiceDocument} inline />
          </div>
        )}
      </Spin>

      <Drawer
        open={mailOpen}
        placement="right"
        onClose={() => setMailOpen(false)}
        width={620}
        rootClassName="mail-popup"
        destroyOnClose
        footer={<div className="mail-popup__footer"><Button onClick={() => setMailOpen(false)}>Cancel</Button><Button type="primary" loading={sending} disabled={!canSend} onClick={handleSend}>Send</Button></div>}
      >
        <CommonMailComposer
          value={mail}
          onChange={setMail}
          moduleName="payments"
          defaultTemplateId={templateId}
          contextRecords={invoice ? [invoice] : []}
          title="Invoice Mail"
          hint="Customer email and invoice attachment are prefilled."
        />
      </Drawer>
    </Drawer>
  );
}
